import React, { useEffect, useRef, useState } from 'react';
import DashboardThumb from './DashboardThumb';

/**
 * One GA dashboard card on the job list (live or archived).
 */
const GaDashboardCard = ({
  dashboard: d,
  folders = [],
  isDragging,
  isOver,
  archived = false,
  onOpen,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragEnd,
  onArchive,
  onRestore,
  onDelete,
  onMoveToFolder,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const close = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [menuOpen]);

  const pageCount = Number(d.settings?.pageCount) || 1;

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      onClick={() => onOpen(d)}
      style={{
        background: archived ? '#f8fafc' : '#fff',
        border: isOver ? '2px solid #0f766e' : '1px solid #e2e8f0',
        borderRadius: 12,
        padding: 12,
        cursor: 'pointer',
        transition: 'box-shadow .15s, opacity .15s, border-color .15s',
        opacity: isDragging ? 0.55 : archived ? 0.88 : 1,
        boxShadow: isOver ? '0 0 0 3px rgba(15,118,110,0.15)' : undefined,
      }}
      onMouseEnter={(e) => { if (!isDragging) e.currentTarget.style.boxShadow = '0 4px 14px rgba(0,0,0,0.1)'; }}
      onMouseLeave={(e) => { if (!isOver) e.currentTarget.style.boxShadow = 'none'; }}
    >
      <DashboardThumb dashboardId={d.id} hasDrawing={!!d.drawing_path} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, minWidth: 0, flex: 1 }}>
          <span
            title="Drag to rearrange or drop onto a folder"
            onClick={(e) => e.stopPropagation()}
            style={{
              cursor: 'grab', color: '#94a3b8', fontSize: 14, lineHeight: 1.2,
              letterSpacing: -1, userSelect: 'none', paddingTop: 2, flexShrink: 0,
            }}
            aria-hidden
          >⠿</span>
          <strong style={{ fontSize: 15, wordBreak: 'break-word' }}>{d.name}</strong>
        </div>
        <div ref={menuRef} style={{ position: 'relative', flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            title="Board actions"
            style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#64748b', fontSize: 18, lineHeight: 1, padding: '0 2px' }}
          >⋯</button>
          {menuOpen && (
            <div style={{
              position: 'absolute', right: 0, top: '100%', zIndex: 20, minWidth: 188,
              background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
              boxShadow: '0 8px 24px rgba(15,23,42,0.12)', padding: 6,
            }}>
              {archived ? (
                <button type="button" onClick={() => { setMenuOpen(false); onRestore(d); }} style={menuBtn}>
                  Restore
                </button>
              ) : (
                <button type="button" onClick={() => { setMenuOpen(false); onArchive(d); }} style={menuBtn}>
                  Archive
                </button>
              )}
              {!archived && (
                <div style={{ borderTop: '1px solid #f1f5f9', margin: '6px 0', paddingTop: 6 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', padding: '2px 8px 4px' }}>Move to</div>
                  <button type="button" onClick={() => { setMenuOpen(false); onMoveToFolder(d, null); }} style={menuBtn}>
                    Unfiled
                  </button>
                  {folders.map((f) => (
                    <button
                      key={f.id}
                      type="button"
                      onClick={() => { setMenuOpen(false); onMoveToFolder(d, f.id); }}
                      style={menuBtn}
                    >
                      {f.name}
                    </button>
                  ))}
                </div>
              )}
              {archived && (
                <button
                  type="button"
                  onClick={() => { setMenuOpen(false); onDelete(d); }}
                  style={{ ...menuBtn, color: '#b91c1c' }}
                >
                  Delete permanently
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      {d.description && <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>{d.description}</div>}
      <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 10, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <span>{d.marker_count} marker{d.marker_count === 1 ? '' : 's'}</span>
        {pageCount > 1 && <span>{pageCount} pages</span>}
        {archived && <span>Archived</span>}
      </div>
    </div>
  );
};

const menuBtn = {
  display: 'block',
  width: '100%',
  textAlign: 'left',
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  padding: '7px 8px',
  borderRadius: 6,
  fontSize: 13,
  color: '#334155',
};

export default GaDashboardCard;
